<template>
  <div class="authorization-wrapper" v-if="props.theme === 'card'">
    <div class="plan" :class="{ disabled: !hasUpdfAuth }">
      <button class="refresh" :class="{ refreshing: planUpdfRefreshing }" @click="handleRefresh"></button>
      <p class="title">{{ $t('UPDF PRO') }}</p>
      <p class="info">{{ $t(hasUpdfAuth ? 'Your account now has full access to all features without limits.' : 'Your account has not been authorized by the administrator') }}</p>
      <p class="status">{{ $t(hasUpdfAuth ? 'Authorized' : 'Unauthorized') }}</p>
      <p class="plan-row last-time" v-if="updfExpireTime && updfExpireTime !== 'Indefinitely'">
        <span class="current-plan" v-if="updfNextPayTime">{{ $t('Renews') }}:</span>
        <span class="current-plan" v-else-if="updfExpireTime">{{ $t('Expire on') }}:</span>
        <span class="expire">{{ updfExpireTime }}</span>
      </p>
    </div>

    <div class="plan-year-ai" :class="ai" v-if="hasAiAuth">
      <button class="refresh" :class="{ refreshing: planAiRefreshing }" @click="handleRefresh"></button>
      <p class="title">{{ $t('UPDF AI Plan') }}</p>
      <p class="info">{{ $t('Full access to all UPDF AI features and UPDF Cloud benefits') }}</p>
      <p class="plan-row current-vip">
        <span class="yearly">{{ $t('Authorized') }}</span>
      </p>
      <p class="plan-row last-time" v-if="aiExpireTime">
        <span class="current-plan" v-if="aiNextPayTime">{{ $t('Renews') }}:</span>
        <span class="current-plan" v-else-if="aiExpireTime">{{ $t('Expire on') }}:</span>
        <span class="expire">{{ aiExpireTime }}</span>
      </p>
    </div>
    <div class="plan-free-ai" v-else>
      <button class="refresh" :class="{ refreshing: planAiRefreshing }" @click="handleRefresh"></button>
      <p class="title">{{ $t('UPDF AI Plan') }}</p>
      <p class="info">{{ $t('Your account has not been authorized by the administrator') }}</p>
      <p class="status">{{ $t('Unauthorized') }}</p>
    </div>

    <div class="plan plan-sign" v-if="hasSignAuth">
      <button class="refresh" :class="{ refreshing: planSignRefreshing }" @click="handleRefresh"></button>
      <p class="title">{{ $t('UPDF Sign') }}</p>
      <p class="info">{{ $t('Your account now has full access to all UPDF Sign features.') }}</p>
      <p class="status">
        <span class="yearly">{{ $t('Authorized') }}</span>
      </p>
      <p class="plan-row last-time" v-if="signExpireTime">
        <span class="current-plan" v-if="signNextPayTime">{{ $t('Renews') }}:</span>
        <span class="current-plan" v-else-if="signExpireTime">{{ $t('Expire on') }}:</span>
        <span class="expire">{{ signExpireTime }}</span>
      </p>
    </div>
  </div>
  <div class="plan-table overflow-x-auto" v-else ref="refTableParent">
    <table ref="refTable">
      <thead>
        <tr>
          <th>{{ $t('Product') }}</th>
          <th>{{ $t('Plan Types') }}</th>
          <th>{{ $t('Details') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="font-bold">{{ $t('UPDF PRO') }}</td>
          <td>{{ $t(hasUpdfAuth ? 'Authorized' : 'Unauthorized') }}</td>
          <td class="color-grey" v-if="updfExpireTime === 'Indefinitely'">
            {{ $t('Your account now has full access to all features without limits.') }}
          </td>
          <td class="color-grey" v-else-if="updfExpireTime || updfNextPayTime">
            <span v-if="updfNextPayTime">{{ $t('Renews') }}:</span>
            <span v-else-if="updfExpireTime">{{ $t('Expire on') }}:</span>
            <span>{{ updfExpireTime }}</span>
          </td>
          <td class="color-grey" v-else>
            {{ $t('Your account has not been authorized by the administrator') }}
          </td>
        </tr>
        <tr>
          <td class="font-bold">{{ $t('UPDF AI Plan') }}</td>
          <td>{{ $t(hasAiAuth ? 'Authorized' : 'Unauthorized') }}</td>
          <td class="color-grey" v-if="aiExpireTime || aiNextPayTime">
            <span v-if="aiNextPayTime">{{ $t('Renews') }}:</span>
            <span v-else-if="aiExpireTime">{{ $t('Expire on') }}:</span>
            <span>{{ aiExpireTime }}</span>
          </td>
          <td class="color-grey" v-else-if="hasAiAuth">
            {{ $t('Full access to all UPDF AI features and UPDF Cloud benefits') }}
          </td>
          <td class="color-grey" v-else>
            {{ $t('Your account has not been authorized by the administrator') }}
          </td>
        </tr>
        <tr v-if="!appStore.isCN">
          <td class="font-bold">{{ $t('UPDF Sign') }}</td>
          <td>{{ $t(hasSignAuth ? 'Authorized' : 'Unauthorized') }}</td>
          <td class="color-grey" v-if="signExpireTime || signNextPayTime">
            <span v-if="signNextPayTime">{{ $t('Renews') }}:</span>
            <span v-else-if="signExpireTime">{{ $t('Expire on') }}:</span>
            <span>{{ signExpireTime }}</span>
          </td>
          <td class="color-grey" v-else-if="hasSignAuth">
            {{ $t('Your account now has full access to all UPDF Sign features.') }}
          </td>
          <td class="color-grey" v-else>
            {{ $t('Your account has not been authorized by the administrator') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup name="company-plan-list">
import { getEmployeeAiAuth, getEmployeeUpdfAuth, getEmployeeSignAuth } from '@/api/company/company'
import { formatTime } from '@/libs/tools'
import { appStore } from '@/stores'
import { onMounted, ref } from 'vue'
const props = defineProps({
  // 主题：card 卡片样式，table 表格样式
  theme: {
    type: String,
    default: 'card',
  },
})

const getPlanExpiredTime = expireTime => {
  if (expireTime === 0) return ''
  if (expireTime === -1) return 'Indefinitely'
  return formatTime(expireTime * 1000, 'L')
}
// 是否有会员
const hasUpdfAuth = ref(false)
const hasAiAuth = ref(false)
const hasSignAuth = ref(false)
// 刷新卡片
const planUpdfRefreshing = ref(false)
const planAiRefreshing = ref(false)
const planSignRefreshing = ref(false)
const emits = defineEmits(['on-loaded'])
// 过期时间
const updfExpireTime = ref(0)
const aiExpireTime = ref(0)
const signExpireTime = ref(0)
// 下次订阅时间
const updfNextPayTime = ref(0)
const aiNextPayTime = ref(0)
const signNextPayTime = ref(0)

const handleGetUpdfAuth = async () => {
  if (planUpdfRefreshing.value) return
  planUpdfRefreshing.value = true
  try {
    const { data } = await getEmployeeUpdfAuth()
    hasUpdfAuth.value = data.hasAuth
    if (data?.plan) {
      updfExpireTime.value = getPlanExpiredTime(data.plan.expireTime)
      updfNextPayTime.value = data.plan.nextPayTime
    }
    emits('on-loaded', [{ planType: data.hasAuth ? 'lifetime' : 'free' }])
  } catch (err) {
    console.log(err)
  }
  setTimeout(() => {
    planUpdfRefreshing.value = false
  }, 1000)
}

const handleGetAiAuth = async () => {
  if (planAiRefreshing.value) return
  planAiRefreshing.value = true
  try {
    const { data } = await getEmployeeAiAuth()
    if (data?.plan) {
      hasAiAuth.value = true
      aiExpireTime.value = getPlanExpiredTime(data.plan.expireTime)
      aiNextPayTime.value = data.plan.nextPayTime
    } else hasAiAuth.value = false
  } catch (error) {
    console.log(error)
  }
  setTimeout(() => {
    planAiRefreshing.value = false
  }, 1000)
}
const handleGetSignAuth = async () => {
  if (planSignRefreshing.value) return
  planSignRefreshing.value = true
  try {
    const { data } = await getEmployeeSignAuth()
    if (data?.plan) {
      hasSignAuth.value = true
      signExpireTime.value = getPlanExpiredTime(data.plan.expireTime)
      signNextPayTime.value = data.plan.nextPayTime
    } else hasSignAuth.value = false
  } catch (error) {
    console.log(error)
  }
  setTimeout(() => {
    planSignRefreshing.value = false
  }, 1000)
}
const handleRefresh = () => {
  handleGetUpdfAuth()
  handleGetAiAuth()
  handleGetSignAuth()
}
onMounted(() => {
  handleRefresh()
})
</script>

<style lang="scss" scoped>
.authorization-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}
.plan,
.plan-free-ai,
.plan-year-ai {
  position: relative;
  width: 288px;
  max-width: 288px;
  box-sizing: border-box;
  padding: 16px;
  background: url(@/assets/subscribe-card-quarterly-1.png) center no-repeat;
  background-size: cover;
  color: #fff;
  border-radius: 8px;
  .title {
    font-size: 16px;
    font-weight: bold;
  }
  .info {
    font-size: 12px;
    line-height: 150%;
    opacity: 0.7;
    margin-top: 12px;
  }
  .status {
    padding: 2px 0;
    font-weight: 860;
    font-size: 24px;
    line-height: 29px;
    text-align: right;
    margin-top: 64px;
  }
  .plan-row {
    font-size: 14px;
    padding: 2px 0;
    text-align: right;
  }
  &.disabled {
    opacity: 0.5;
  }
  .refresh {
    width: 24px;
    height: 24px;
    background: url(@/assets/subscribe-refresh.png) center no-repeat;
    background-size: cover;
    position: absolute;
    right: 12px;
    top: 12px;
    border: none;
    cursor: pointer;
    transition: all 0.5s;
    &.refreshing {
      animation: 1.5s linear 0s normal none infinite animation-rotate;
    }
  }
  &.plan-sign {
    background: url(@/assets/subscribe-card-esign-monthly-plan.png) center no-repeat;
    background-size: cover;
  }
}

.ar-SA {
  .plan,
  .plan-year-ai,
  .plan-free-ai {
    .refresh {
      left: 12px;
      right: unset;
    }
  }
}

.plan-year-ai {
  max-width: 288px;
  width: 100%;
  border-radius: 8px;
  background: url(@/assets/subscribe-card-ai-year-1.png) center no-repeat;
  background-size: cover;
  position: relative;
  .current-vip {
    margin-top: 64px;
  }
  .updf-icon {
    position: absolute;
    width: 156px;
    right: 0;
    top: 0;
    filter: grayscale(1);
  }
  .yearly {
    font-size: 24px;
    font-weight: 900;
    margin-left: 3px;
  }
  .expire {
    margin-left: 3px;
  }
  .plan-row {
    font-size: 14px;
    padding: 2px 0;
    text-align: right;
    &.accross-device {
      display: flex;
      justify-content: right;
      align-items: center;
      margin-top: 2px;
      .label {
        align-self: flex-start;
        width: auto;
        line-height: 24px;
        word-wrap: break-word;
      }
      span {
        display: inline-block;
        width: 24px;
        height: 24px;
        background-size: cover;
        margin: 0 2px;
        flex: none;
        &.macOS {
          background-image: url(@/assets/plan-devicetype-macOS.png);
        }
        &.android {
          background-image: url(@/assets/plan-devicetype-android.png);
        }
        &.iOS {
          background-image: url(@/assets/plan-devicetype-iOS.png);
        }
        &.windows {
          background-image: url(@/assets/plan-devicetype-windows.png);
        }
      }
    }
  }
}
.plan-free-ai {
  background: url(@/assets/subscribe-card-ai-free-1.png) center no-repeat;
  background-size: cover;
}

@media screen and (max-width: 640px) {
  .plan {
    width: 100%;
  }
}
</style>
